/**
 * Client Intelligence hub — top-level page listing all clients
 * across all divisions with their headline customer-intelligence metrics.
 *
 * Access (Dipali doctrine 30-Apr-2026):
 *   - CEO + the Concierge® team (Nisha, Samar, Shiva, Tanisha) — see
 *     `lib/permissions.js#canAccessClientIntel`.
 *   - Other leaders / sub-leaders reach the per-client dashboard via
 *     Division → Clients tab → Client Dossier → Intelligence tab.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canAccessClientIntel } from '@/lib/permissions';
import CustomerIntelligenceDashboard from '@/components/CustomerIntelligenceDashboard';
import { ChevronLeft, Sparkles, Users, Activity, ArrowRight } from 'lucide-react';

const fmtN = (v) => (Number(v) || 0).toLocaleString('en-IN');
const fmtPct = (v) => `${(Number(v) || 0).toFixed(1)}%`;

export default function ClientIntelligenceHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (clientId) return; // we're in drill-down view; skip list fetch
    api.get('/customer-intelligence/_meta/clients')
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'Failed to load clients'));
  }, [clientId]);

  if (!canAccessClientIntel(user)) {
    return (
      <div className="p-8 text-sm" style={{ color: '#A32D2D' }}>
        Customer Intelligence hub is restricted. Leaders access per-client data
        through their division's Client Dossier → Intelligence tab.
      </div>
    );
  }

  // Drill-down view
  if (clientId) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/client-intelligence')}
          className="flex items-center gap-1 text-sm hover:opacity-70"
          style={{ color: '#2A7B88' }}
          data-testid="ci-back-to-hub">
          <ChevronLeft size={16} /> Back to all clients
        </button>
        <CustomerIntelligenceDashboard clientId={clientId} />
      </div>
    );
  }

  // Hub list view
  if (error) return <div className="p-6 text-sm" style={{ color: '#A32D2D' }}>{error}</div>;
  if (!data) return <div className="p-6 text-sm" style={{ color: '#9CA3AF' }}>Loading…</div>;

  const phaseLabel = (p) => p === 'active' ? '🟢 Active' : p === 'phase2' ? '🟡 Phase 2' : '⚪ Phase 3';

  return (
    <div className="space-y-5 fade-in" data-testid="client-intelligence-hub">
      <div>
        <div className="flex items-center gap-2">
          <Sparkles size={20} style={{ color: '#2A7B88' }} />
          <h1 className="text-2xl font-bold" style={{ color: '#1B2A4A' }}>Client Intelligence</h1>
        </div>
        <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
          Live customer intelligence pulled nightly from Zoho Desk. Click any active client to drill into pillar-level engagement, geography, tiers, and the auto-generated monthly report.
        </p>
        <p className="text-[11px] mt-1" style={{ color: '#9CA3AF' }}>Current month: <strong>{data.current_month}</strong></p>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
        <table className="w-full text-[13px]">
          <thead>
            <tr style={{ background: '#F7F5F2', color: '#6B7280' }}>
              <th className="py-3 px-4 text-left text-[11px] uppercase tracking-wider">Client</th>
              <th className="py-3 px-4 text-left text-[11px] uppercase tracking-wider">Division</th>
              <th className="py-3 px-4 text-left text-[11px] uppercase tracking-wider">Phase</th>
              <th className="py-3 px-4 text-right text-[11px] uppercase tracking-wider">Members</th>
              <th className="py-3 px-4 text-right text-[11px] uppercase tracking-wider">Requests ({data.current_month?.slice(5)})</th>
              <th className="py-3 px-4 text-right text-[11px] uppercase tracking-wider">Engagement</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {data.clients.map(c => (
              <tr
                key={c.client_id}
                onClick={() => c.phase === 'active' && navigate(`/client-intelligence/${c.client_id}`)}
                className={`border-t transition-colors ${c.phase === 'active' ? 'cursor-pointer hover:bg-[#F7F5F2]' : 'opacity-60'}`}
                style={{ borderColor: '#F0EEEA' }}
                data-testid={`ci-row-${c.client_id}`}>
                <td className="py-3 px-4 font-semibold" style={{ color: '#1B2A4A' }}>{c.client_name}</td>
                <td className="py-3 px-4" style={{ color: '#6B7280' }}>{c.division_id}</td>
                <td className="py-3 px-4 text-[11px]" style={{ color: '#6B7280' }}>{phaseLabel(c.phase)}</td>
                <td className="py-3 px-4 text-right tabular-nums" style={{ color: '#1B2A4A' }}>
                  {c.has_data ? <>{fmtN(c.active_members)} <span style={{ color: '#9CA3AF' }}>/ {fmtN(c.total_members)}</span></> : '—'}
                </td>
                <td className="py-3 px-4 text-right tabular-nums" style={{ color: '#1B2A4A' }}>
                  {c.has_data ? fmtN(c.requests_this_month) : '—'}
                </td>
                <td className="py-3 px-4 text-right tabular-nums">
                  {c.has_data ? <span className={c.engagement_pct >= 25 ? 'text-emerald-700 font-semibold' : 'text-stone-700'}>{fmtPct(c.engagement_pct)}</span> : '—'}
                </td>
                <td className="py-3 px-4 text-right">
                  {c.phase === 'active'
                    ? <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: '#2A7B88' }}>View <ArrowRight size={12} /></span>
                    : <span className="text-[11px]" style={{ color: '#9CA3AF' }}>Soon</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
        <MiniStat icon={Users}    label="Active programmes" value={data.clients.filter(c => c.phase === 'active').length} />
        <MiniStat icon={Activity} label="Total tracked members" value={fmtN(data.clients.reduce((s,c) => s + (c.total_members || 0), 0))} />
        <MiniStat icon={Sparkles} label={`Requests in ${data.current_month}`} value={fmtN(data.clients.reduce((s,c) => s + (c.requests_this_month || 0), 0))} />
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>
        <Icon size={11} /> {label}
      </div>
      <div className="text-xl font-bold mt-1" style={{ color: '#1B2A4A' }}>{value}</div>
    </div>
  );
}
